import { Injectable } from '@angular/core';
import { isValidTimezone, formatDateInTimezone, getTimezoneAbbreviation } from '../utilities/timezone.util';
import { OrganizationType } from '../models/organization-type';
import { DEFAULT_DEPOT_ID } from '../constants/default.const';
import { OrganizationDepot } from '../models/organization-depot.model';
import { DepotTimezoneDateFormat } from '../enums/depot-timezone-date-format.enum';

/**
 * Depot timezone date formatter service - SINGLE SOURCE OF TRUTH for timezone formatting logic.
 *
 * This service contains ALL timezone formatting business logic in ONE place.
 * Used by:
 * - AG Grid cell renderers (synchronous usage)
 * - DepotTimezoneDatePipe (wraps in Observable for templates)
 *
 * Benefits of centralized logic:
 * - DRY: No code duplication
 * - Maintainability: Changes only in one place
 * - Consistency: Same logic everywhere
 * - Testability: Test once, works everywhere
 *
 * Organization Type Behavior:
 * - Expert: Uses depot timezone (converts to depot's local time) with timezone labels
 * - Syrinx: Uses UTC (NO timezone conversion - shows times exactly as API returns) WITHOUT timezone labels
 * - Essential: Uses browser timezone with timezone labels
 */
@Injectable({
    providedIn: 'root'
})
export class DepotTimezoneFormatterService {
    /**
     * Main formatting method that handles all format types.
     *
     * @param date - Date to format (Date object, ISO string, or timestamp)
     * @param format - Format type (use DepotTimezoneDateFormat enum)
     * @param organizationType - Organization type (Expert, Syrinx, Essential)
     * @param depots - Array of organization depots with timezone info
     * @param depotId - Optional depot ID. Defaults to '001' if not provided.
     * @param locale - Locale to use for formatting (e.g., "en-US", "en-GB"). Defaults to "en-US" for backward compatibility.
     * @returns Formatted date string
     *
     * @example
     * ```typescript
     * // Full format with depot timezone (US locale)
     * formatter.format('2025-10-29T02:51:54Z', DepotTimezoneDateFormat.Full, OrganizationType.EXPERT, depots, '002', 'en-US')
     * // Returns: "Oct 28, 2025, 10:51 PM EDT"
     *
     * // Full format with depot timezone (UK locale)
     * formatter.format('2025-10-29T02:51:54Z', DepotTimezoneDateFormat.Full, OrganizationType.EXPERT, depots, '002', 'en-GB')
     * // Returns: "28 Oct 2025, 22:51 EDT"
     *
     * // Date only
     * formatter.format('2025-10-29T02:51:54Z', DepotTimezoneDateFormat.DateOnly, OrganizationType.EXPERT, depots, '002', 'en-US')
     * // Returns: "10/28/2025"
     * ```
     */
    format(
        date: Date | string | number | null | undefined,
        format: DepotTimezoneDateFormat,
        organizationType: OrganizationType | undefined,
        depots: OrganizationDepot[] | undefined,
        depotId?: string,
        locale = 'en-US'
    ): string {
        if (!date) {
            return '';
        }

        // SYRINX COMPATIBILITY: Syrinx uses UTC (no conversion) and never shows timezone labels
        const isSyrinx = organizationType === OrganizationType.SYRINX;

        // FEATURE WRAPPING: Only Expert RMS uses depot timezone
        const isExpert = organizationType === OrganizationType.EXPERT;

        // Determine which timezone to use:
        // - Expert: If depotId parameter provided, use that depot's timezone; otherwise default to DEFAULT_DEPOT_ID
        // - Syrinx: Always use UTC (no timezone conversion)
        // - Essential/others: Use browser timezone
        let depotTimezone: string | undefined;

        if (isExpert && depots && depots.length > 0) {
            // Use passed depotId parameter if provided, otherwise default to DEFAULT_DEPOT_ID
            const targetDepotId = depotId || DEFAULT_DEPOT_ID;
            const targetDepot = depots.find(d => d.Id === targetDepotId);
            depotTimezone = targetDepot?.TimeZone;
        }

        const shouldUseDepotTimezone = isExpert && isValidTimezone(depotTimezone);

        // Determine timezone based on organization type:
        // - Expert: Use depot timezone (converted to depot's local time)
        // - Syrinx: Use UTC (NO conversion - show times exactly as API returns them)
        // - Essential/others: Use browser timezone
        const timezone = shouldUseDepotTimezone && depotTimezone ? depotTimezone : isSyrinx ? 'UTC' : Intl.DateTimeFormat().resolvedOptions().timeZone;

        const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

        // Determine hour12 based on locale:
        // en-GB (UK) = 24-hour format (hour12: false)
        // en-US (US) = 12-hour format (hour12: true)
        // Default to 12-hour for backward compatibility
        const hour12 = locale !== 'en-GB';

        // Format based on requested format
        let formattedDate: string;

        switch (format) {
            case DepotTimezoneDateFormat.Short:
                formattedDate = formatDateInTimezone(
                    dateObj,
                    timezone,
                    {
                        year: '2-digit',
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12
                    },
                    locale
                );
                break;

            case DepotTimezoneDateFormat.DateOnly:
                formattedDate = formatDateInTimezone(
                    dateObj,
                    timezone,
                    {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                    },
                    locale
                );
                break;

            case DepotTimezoneDateFormat.TimeOnly: {
                const timeStr = formatDateInTimezone(
                    dateObj,
                    timezone,
                    {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12
                    },
                    locale
                );
                // SYRINX: Don't show timezone label
                if (isSyrinx) {
                    formattedDate = timeStr;
                } else {
                    const tzAbbr = getTimezoneAbbreviation(timezone, dateObj, locale);
                    formattedDate = `${timeStr} ${tzAbbr}`;
                }
                break;
            }

            case DepotTimezoneDateFormat.Full:
            default: {
                const fullDate = formatDateInTimezone(
                    dateObj,
                    timezone,
                    {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12
                    },
                    locale
                );
                // SYRINX: Don't show timezone label
                if (isSyrinx) {
                    formattedDate = fullDate;
                } else {
                    const tzAbbrFull = getTimezoneAbbreviation(timezone, dateObj, locale);
                    formattedDate = `${fullDate} ${tzAbbrFull}`;
                }
                break;
            }
        }

        return formattedDate;
    }

    /**
     * Convenience method for full format (default).
     * Equivalent to format(date, DepotTimezoneDateFormat.Full, ...)
     */
    formatFull(date: Date | string | number | null | undefined, organizationType: OrganizationType | undefined, depots: OrganizationDepot[] | undefined, depotId?: string, locale = 'en-US'): string {
        return this.format(date, DepotTimezoneDateFormat.Full, organizationType, depots, depotId, locale);
    }

    /**
     * Convenience method for short format.
     * Equivalent to format(date, DepotTimezoneDateFormat.Short, ...)
     */
    formatShort(date: Date | string | number | null | undefined, organizationType: OrganizationType | undefined, depots: OrganizationDepot[] | undefined, depotId?: string, locale = 'en-US'): string {
        return this.format(date, DepotTimezoneDateFormat.Short, organizationType, depots, depotId, locale);
    }

    /**
     * Convenience method for date only format (no time).
     * Equivalent to format(date, DepotTimezoneDateFormat.DateOnly, ...)
     */
    formatDateOnly(
        date: Date | string | number | null | undefined,
        organizationType: OrganizationType | undefined,
        depots: OrganizationDepot[] | undefined,
        depotId?: string,
        locale = 'en-US'
    ): string {
        return this.format(date, DepotTimezoneDateFormat.DateOnly, organizationType, depots, depotId, locale);
    }

    /**
     * Convenience method for time only format.
     * Equivalent to format(date, DepotTimezoneDateFormat.TimeOnly, ...)
     */
    formatTimeOnly(
        date: Date | string | number | null | undefined,
        organizationType: OrganizationType | undefined,
        depots: OrganizationDepot[] | undefined,
        depotId?: string,
        locale = 'en-US'
    ): string {
        return this.format(date, DepotTimezoneDateFormat.TimeOnly, organizationType, depots, depotId, locale);
    }
}
